'use es6';

import Immutable from 'immutable';
import { useMemo, useEffect, useState } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import { getReportVisualType } from 'reporting-snowflake/relational/utils/report-utils';
import { VisualTypes } from 'reporting-snowflake/relational/schema/visual-records';
import { ReportOptions } from 'reporting-snowflake/relational/resolve/resolve-records';
export const API_LIMIT = 1000;
export const clampLimitAndOffset = reportOptions => {
  const offset = API_LIMIT * Math.floor((reportOptions.offset || 0) / API_LIMIT);
  return reportOptions.set('limit', API_LIMIT).set('offset', offset);
};
const getInitialLimit = reportDefinition => getReportVisualType(reportDefinition) === VisualTypes.TABLE ? 10 : API_LIMIT;
export const createDefaultReportOptions = (reportDefinition, maybeReportId) => ReportOptions({
  limit: getInitialLimit(reportDefinition),
  offset: 0,
  fetchFromCache: maybeReportId !== null && maybeReportId !== undefined
});
export const createDefaultDrilldownReportOptions = reportDefinition => createDefaultReportOptions(reportDefinition).set('isDrilldownRequest', true);
const useCreateDefaultInitialOptions = (initialReportOptions, reportId, reportDefinition) => useMemo(() => {
  const defaultReportOptions = createDefaultReportOptions(reportDefinition, reportId);
  return initialReportOptions ? ReportOptions(initialReportOptions).mergeWith((initialValue, defaultValue) => initialValue === undefined ? defaultValue : initialValue, defaultReportOptions) : defaultReportOptions;
}, [initialReportOptions, reportDefinition, reportId]);

/**
 * Manages the state of ReportOptions
 * @param {Map} initialReportOptions - a Map holding report options
 * @param {number?} reportId
 * @param {RelationalReport} reportDefinition
 * @returns {[ReportOptions, setterFn]}
 */
export const useReportOptions = (initialReportOptions, reportId, reportDefinition) => {
  const [reportOptions, setReportOptions] = useState(initialReportOptions);
  const initialReportOptionsWithDefault = useCreateDefaultInitialOptions(initialReportOptions, reportId, reportDefinition);

  // when initialReportOptions change in value, force a reset on the report options
  const previousInitialReportOptions = usePrevious(initialReportOptions);
  useEffect(() => {
    if (!Immutable.is(initialReportOptions, previousInitialReportOptions)) {
      setReportOptions(initialReportOptionsWithDefault);
    }
  }, [initialReportOptions, previousInitialReportOptions, initialReportOptionsWithDefault]);

  // when reportDefinition change in value, force a reset on the report options
  const previousReportDefinition = usePrevious(reportDefinition);
  useEffect(() => {
    if (reportDefinition !== previousReportDefinition) {
      setReportOptions(initialReportOptionsWithDefault);
    }
  }, [reportDefinition, previousReportDefinition, initialReportOptionsWithDefault]);
  return [reportOptions, setReportOptions];
};